<template>
  <template v-if="$route.name === 'classes.index'">
    <ClassFilter @search="handleSearch" />

    <ClassTable
      :classes="classes"
      :loading="isLoading"
      @edit="openEditDialog"
      @delete="openDeleteDialog"
    />
    
    <PaginationControls
      :pagination="pagination"
      @page-changed="handlePageChange"
      @per-page-changed="handlePerPageChange"
    />
    
    <DeleteClassDialog ref="deleteDialog" @delete="handleDeleteClass" />
  </template>
  
  <router-view></router-view>
</template>
<script setup>
import { ref, computed, onMounted } from "vue";
import { useStore } from "vuex";
import { useI18n } from 'vue-i18n';
import ClassTable from "@/components/features/classes/ClassTable.vue";
import PaginationControls from "@/components/common/PaginationControls.vue";
import DeleteClassDialog from "@/components/features/classes/dialogs/DeleteClassDialog.vue";
import ClassFilter from "@/components/features/classes/filters/ClassFilter.vue";
import { useToast } from 'vue-toastification';
import api from "@/api";

const { t, locale } = useI18n();
const store = useStore();
const toast = useToast();

const isLoading = ref(false);

// State
const classes = computed(() => store.state.classes.classes);
const pagination = computed(() => store.state.classes.pagination);
const searchTerm = ref("");

// Dialog refs
const deleteDialog = ref(null);
const selectedClassId = ref(null);

/* ---------- helpers: consistent error toasts ---------- */
function extractApiMessage(error, fallbackKey = 'errors.generic') {
  const data = error?.response?.data;
  if (data?.message) {
    if (typeof data.message === 'object' && data.message !== null) {
      const loc = (data.locale || locale.value || 'en').toLowerCase().startsWith('ar') ? 'ar' : 'en';
      if (typeof data.message[loc] === 'string') return data.message[loc];
    }
    if (typeof data.message === 'string') return data.message;
  }
  if (error?.response?.statusText) return error.response.statusText;
  return t(fallbackKey);
}

function notifyError(error, fallbackKey = 'errors.generic') {
  const s = error?.response?.status;
  if (s === 401) return toast.error(t('auth.unauthorized', 'Unauthorized'));
  if (s === 403) return toast.error(t('auth.forbidden', 'Forbidden')); // <- explicit forbidden
  return toast.error(extractApiMessage(error, fallbackKey));
}

/* ---------- actions ---------- */
const fetchClasses = async (page = 1, perPage = 10, search = '') => {
  isLoading.value = true;
  try {
    await store.dispatch("classes/fetchClasses", { 
      page, 
      per_page: perPage, 
      search 
    });
  } catch (error) {
    notifyError(error, 'classes.messages.loadError'); // <- Forbidden on 403
    console.error("Error fetching classes:", error);
  } finally {
    isLoading.value = false;
  }
};

const openDeleteDialog = (id) => {
  selectedClassId.value = id;
  deleteDialog.value?.openDialog();
};

const handlePageChange = (page) => {
  const perPage = pagination.value.perPage ?? pagination.value.per_page ?? 10;
  fetchClasses(page, perPage, searchTerm.value);
};

const handlePerPageChange = (perPage) => {
  fetchClasses(1, perPage, searchTerm.value); 
};

const handleDeleteClass = async () => {
  isLoading.value = true;
  try {
    await api.delete(`/classes/${selectedClassId.value}`);
    await fetchClasses(pagination.value.currentPage ?? 1, pagination.value.perPage ?? pagination.value.per_page ?? 10, searchTerm.value);
    toast.success(t('common.deleteSuccess'));
  } catch (e) {
    if (e?.response?.status === 403) {
      toast.error(t('auth.forbidden', 'Forbidden')); // <- Forbidden on delete
    } else {
      const msg =
        e?.response?.data?.errors?.class?.[0] ||
        extractApiMessage(e, 'errors.deleteFailed');
      toast.error(msg);
    }
    console.error('Delete error:', e);
  } finally {
    isLoading.value = false;
  }
};

let searchTimeout = null;
const handleSearch = (term) => {
  searchTerm.value = term;
  clearTimeout(searchTimeout);
  const perPage = pagination.value.perPage ?? pagination.value.per_page ?? 10;
  searchTimeout = setTimeout(() => {
    fetchClasses(1, perPage, term);
  }, 300);
};

onMounted(() => fetchClasses());
</script>
